"use client";
import {
    categoryTitleMobileStyles,
    mobileButtonBackStyles,
    navItemMobileStyles,
    parentThumbnailContainerStyles,
    salesCategoryTitleMobileStyles,
    salesMobileButtonBackStyles,
    salesNavItemMobileStyles,
    seeAllCategoriesMobileStyles,
    thumbnailCarouselContainerStyles,
    thumbnailCarouselItemStyles,
} from "@components/layouts/navigation/styles";
import type { NavigationItem } from "@lib/contentful/mappers/mapNavigation";
import { Text } from "@ecco/ui/text";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { Container } from "@ecco/ui/container";
import { Flex } from "@ecco/ui/flex";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { NextImage } from "@ecco/ui/next-image";
import { useDisclosure } from "@ecco/ui/use-disclosure";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { CategoryTile } from "../components/CategoryTile";
import { MobileSubNavItem } from "./MobileSubNavItem";
import {
    FIRST_LEVEL_NAVIGATION_BUTTON,
    MENU_DROPDOWN,
    SECOND_LEVEL_NAVIGATION_LINK,
} from "@e2e/helpers/navigation/constants";
import { ContentLink } from "@components/cms/link/ContentLink";
import { usePathname } from "next/navigation";

export interface MobileNavItemProps {
    navigationItem: NavigationItem;
    handleFirstMenuBgColor: () => void;
}

export const MobileNavItem = ({ navigationItem, handleFirstMenuBgColor }: MobileNavItemProps) => {
    const { getButtonProps, getDisclosureProps, isOpen, onClose } = useDisclosure();
    const [hidden, setHidden] = useState(!isOpen);
    const asPath = usePathname();

    const handleMotionStart = (onAnimationStartStatus: boolean) => {
        handleFirstMenuBgColor();
        setHidden(onAnimationStartStatus);
    };

    const getMobileNavigationStyles = (isSales: boolean) => ({
        navItemMobileStyles: isSales ? salesNavItemMobileStyles : navItemMobileStyles,
        categoryTitleMobileStyles: isSales
            ? salesCategoryTitleMobileStyles
            : categoryTitleMobileStyles,
        mobileButtonBackStyles: isSales ? salesMobileButtonBackStyles : mobileButtonBackStyles,
        iconColor: "black",
    });

    useEffect(() => {
        const interval = setTimeout(() => {
            onClose();
        }, 1000);
        return () => clearTimeout(interval);
    }, [asPath, onClose]);

    const { link, menu, isSales } = navigationItem;
    const columns = menu?.columns ?? [];
    const thumbnailLinks = menu?.thumbnailLinks ?? [];
    const tiles = menu?.tiles ?? [];
    const fallbackMedia = menu?.fallbackMedia;
    const seeAllLabel = menu?.seeAllLabel;
    const hasNestedContent = Boolean(menu);

    if (!hasNestedContent) {
        return (
            <ContentLink
                data-testid={FIRST_LEVEL_NAVIGATION_BUTTON}
                link={link.contentLink}
                sx={getMobileNavigationStyles(isSales).navItemMobileStyles}
            >
                <Text sx={getMobileNavigationStyles(isSales).categoryTitleMobileStyles}>
                    {link.label}
                </Text>
            </ContentLink>
        );
    }

    return (
        <Flex direction="column" data-testid={link.testId || link.label || "navigation-item"}>
            <Flex
                data-testid={FIRST_LEVEL_NAVIGATION_BUTTON}
                as={Button}
                colorScheme="white"
                _hover={{ bg: "transparent" }}
                _active={{ bg: "transparent" }}
                _focus={{ bg: "transparent" }}
                variant="solid"
                {...getButtonProps()}
                sx={getMobileNavigationStyles(isSales).navItemMobileStyles}
            >
                <Text sx={getMobileNavigationStyles(isSales).categoryTitleMobileStyles}>
                    {link.label}
                </Text>
                <ECCOIcon
                    aria-label="Right arrow"
                    name="chevron"
                    color="black"
                    transform="rotate(-90deg)"
                />
            </Flex>
            <motion.div
                {...(getDisclosureProps() as any)}
                hidden={hidden}
                initial={false}
                data-testid={MENU_DROPDOWN}
                onAnimationStart={() => handleMotionStart(false)}
                onAnimationComplete={() => setHidden(!isOpen)}
                animate={{ left: isOpen ? "0%" : "100%" }}
                transition={{ ease: "easeOut", duration: 0.15 }}
                style={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    position: "absolute",
                    right: "0",
                    top: "0",
                    zIndex: 3,
                    width: "100%",
                    marginTop: 0,
                }}
            >
                {/* Second Level Navigation Mobile — back button */}
                <Flex
                    as={Button}
                    colorScheme="white"
                    variant="solid"
                    sx={getMobileNavigationStyles(isSales).mobileButtonBackStyles}
                    {...getButtonProps()}
                >
                    <ECCOIcon
                        aria-label="Left arrow"
                        name="chevron"
                        color="black"
                        transform="rotate(90deg)"
                    />
                    {/* Removed hardcoded fontWeight="semibold" — now controlled by styles */}
                    <Text fontSize="desktopHeading5">{link.label}</Text>
                </Flex>
                <Container h="100%" overflow="auto" marginTop="1px" bg="white" px="0" maxW="100%">
                    {thumbnailLinks.length > 0 ? (
                        <Box {...parentThumbnailContainerStyles} data-testid="ThumbnailLinks">
                            <Flex sx={thumbnailCarouselContainerStyles}>
                                <Flex {...thumbnailCarouselItemStyles}>
                                    {thumbnailLinks.map((link) => (
                                        <CategoryTile
                                            key={link.id}
                                            link={link}
                                            isLeftSide={true}
                                            imageSizes={["200vw"]}
                                        />
                                    ))}
                                </Flex>
                            </Flex>
                        </Box>
                    ) : (
                        <>
                            {seeAllLabel && (
                                <ContentLink
                                    data-testid={SECOND_LEVEL_NAVIGATION_LINK}
                                    link={link.contentLink}
                                    sx={seeAllCategoriesMobileStyles}
                                >
                                    {seeAllLabel}
                                </ContentLink>
                            )}
                            {columns.length > 0 &&
                                columns.map((column) => (
                                    <MobileSubNavItem
                                        key={column.id}
                                        title={column.title}
                                        links={column.links}
                                    />
                                ))}
                        </>
                    )}
                    <Flex flexDir="column" gap={8} justifyContent="space-between" pb={4} px={4}>
                        {tiles.length > 0 ? (
                            tiles.map((tile) => (
                                <CategoryTile
                                    key={tile.id}
                                    link={tile}
                                    imageSizes={["100vw"]}
                                    isVisible={isOpen}
                                />
                            ))
                        ) : fallbackMedia ? (
                            <NextImage
                                objectFit="cover"
                                src={fallbackMedia.url}
                                alt={fallbackMedia.alt}
                                sizes={["100vw"]}
                            />
                        ) : null}
                    </Flex>
                </Container>
            </motion.div>
        </Flex>
    );
};
